import * as uuid from 'uuid';

import { type FC } from 'react';
import { useSelector } from 'react-redux';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { Link } from 'react-router-dom';

import { type RootState } from '@editor2/reducers/root';
import ButtonTooltip from '../ButtonTooltip';
import { navigationBlock } from './styles';
import { type Chapter } from '@editor2/types/chapter';
import { type PageGroup } from '@editor2/types/pageGroup';
import { type Page } from '@editor2/types/page';
import { getChapterOrdinalNumber, getPageOrdinalNumber } from '@editor2/services/numberingService';

interface Props {
	page?: Page;
	pageGroup?: PageGroup;
	chapter?: Chapter;
	courseId: number;
}

const Navigation: FC<Props> = (props) => {
	const { chapter, pageGroup, page, courseId } = props;

	const { chapters, page_groups, pages } = useSelector(
		(state: RootState) => state.courses[courseId]
	);
	const courseState = useSelector((state: RootState) => state.courses[courseId]);

	const getNavigationData = () => {
		switch (true) {
			case !!page: {
				const ids =
					chapters.entities[chapter.family_id]?.pages ||
					page_groups.entities[pageGroup.family_id].pages;
				const index = ids.indexOf(page.family_id);
				return { ids, index };
			}

			case !!pageGroup: {
				const ids = chapters.entities[chapter.family_id].page_groups;
				const index = ids.indexOf(pageGroup.family_id);
				return { ids, index };
			}

			case !!chapter: {
				const ids = chapters.ids;
				const index = ids.indexOf(chapter.family_id);
				return { ids, index };
			}

			default:
				break;
		}
	};

	const getLinkPrefix = () => {
		return (
			`/courses/${courseId}/chapters` +
			(pageGroup ? `/${chapter.family_id}/page_groups` : '') +
			(page ? `/${pageGroup?.family_id || chapter.family_id}/pages` : '')
		);
	};

	const getCurrentPageName = () => {
		return (page || pageGroup || chapter).name;
	};

	const getEntityNumber = (id: string) => {
		switch (true) {
			case !!page:
				return pages.entities[id].page_number || `#${getPageOrdinalNumber(courseState, id)}`;

			case !!pageGroup:
				return;

			case !!chapter:
				return chapters.entities[id].chapter_number || `#${getChapterOrdinalNumber(chapters, id)}`;

			default:
				break;
		}
	};

	const getCurrentPageNamePrefix = (id: string) => {
		const splitType = (page || pageGroup || chapter).type.split('::');
		const type = splitType[splitType.length - 1];

		const number = getEntityNumber(id);
		return type + (number && number !== ' ' ? ` ${number}: ` : ': ');
	};

	const getEntityNameById = (id: string) => {
		switch (true) {
			case !!page:
				return pages.entities[id].name;

			case !!pageGroup:
				return page_groups.entities[id].name;

			case !!chapter:
				return chapters.entities[id].name;

			default:
				break;
		}
	};

	const { ids, index } = getNavigationData();

	return (
		<div className={navigationBlock}>
			<div>
				{index !== 0 && (
					<ButtonTooltip message={getEntityNameById(ids[index - 1])} placement="left">
						<Link
							to={{
								pathname: `${getLinkPrefix()}/${ids[index - 1]}`,
								key: uuid.v4(),
								state: { applied: true }
							}}
							replace>
							<BsArrowLeft className="prev" />
						</Link>
					</ButtonTooltip>
				)}
			</div>

			<div>
				<span className="pageType">{getCurrentPageNamePrefix(ids[index])}</span>
				{getCurrentPageName()}
			</div>

			<div>
				{index !== ids.length - 1 && (
					<ButtonTooltip message={getEntityNameById(ids[index + 1])} placement="right">
						<Link
							to={{
								pathname: `${getLinkPrefix()}/${ids[index + 1]}`,
								key: uuid.v4(),
								state: { applied: true }
							}}
							replace>
							<BsArrowRight className="next" />
						</Link>
					</ButtonTooltip>
				)}
			</div>
		</div>
	);
};

export default Navigation;
